import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-coronis text-sm font-medium transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-coronis-blue-500 focus-visible:ring-offset-2 dark:ring-offset-slate-900 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-coronis-navy-600 text-white hover:bg-coronis-navy-700 active:bg-coronis-navy-800 dark:bg-coronis-blue-600 dark:hover:bg-coronis-blue-700",
        primary:
          "bg-coronis-navy-700 text-white border border-coronis-blue-500/60 hover:bg-coronis-navy-600 hover:border-coronis-blue-400/70 active:bg-coronis-navy-800 active:border-coronis-blue-600/50",
        destructive: "bg-red-600 text-white hover:bg-red-700 active:bg-red-800",
        outline:
          "border border-coronis-navy-200 bg-white text-coronis-navy-600 hover:bg-white hover:border-coronis-navy-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 dark:hover:border-slate-500",
        secondary:
          "bg-coronis-blue-100 text-coronis-navy-600 hover:bg-coronis-blue-200 dark:bg-slate-700 dark:text-slate-200 dark:hover:bg-slate-600",
        ghost:
          "text-coronis-navy-600 hover:bg-slate-100 hover:text-coronis-navy-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-slate-100",
        link: "text-coronis-blue-600 underline-offset-4 hover:underline dark:text-coronis-blue-400",
        success: "bg-coronis-teal-600 text-white hover:bg-coronis-teal-700",
        "soft-destructive":
          "bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 dark:bg-red-950 dark:text-red-400 dark:hover:bg-red-900 dark:border-red-800",
        "soft-warning":
          "bg-amber-50 text-amber-600 hover:bg-amber-100 border border-amber-200 dark:bg-amber-950 dark:text-amber-400 dark:hover:bg-amber-900 dark:border-amber-800",
        "soft-blue":
          "bg-coronis-blue-50 text-coronis-blue-600 hover:bg-coronis-blue-100 border border-coronis-blue-200 dark:bg-coronis-blue-950 dark:text-coronis-blue-400 dark:hover:bg-coronis-blue-900 dark:border-coronis-blue-800",
        soft: "bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:border-slate-600",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-12 rounded-coronis-lg px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      children,
      disabled,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
